import React from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { Text } from '@contrast-security-inc/design-system-typography'
import { LibraryReleaseDate } from '../library/library-release-date'
import { LibraryLicenses } from '../library-licenses'
import { RestrictionIcon } from '../cell-content/restriction-icon'
import { Library } from '../../helpers/ui-interfaces'
import { useLibrariesPageContext } from '../../pages'
import {
  TextBar,
  TextBarItem
} from '@contrast-security-inc/design-system-data-bars'

export interface LibraryDetailsProps {
  readonly library: {
    readonly hash: Library['hash']
    readonly versions: {
      readonly current: {
        readonly releaseDate?: Library['versions']['current']['releaseDate']
      }
    }
    readonly ossEnabled: Library['ossEnabled']
    readonly licenses: Library['licenses']
    readonly policies: Library['policies']
  }
  readonly className?: string
}

export const showRestrictionIndicator = ({
  restrictedLicense,
  restrictedByOrg,
  versionRestriction
}: {
  readonly restrictedLicense: Library['policies']['restrictedLicense']
  readonly restrictedByOrg: Library['policies']['restrictedByOrg']
  readonly versionRestriction: Library['policies']['versionRestriction']
}) => restrictedLicense || restrictedByOrg || versionRestriction

export const LibraryDetails = ({
  library: {
    hash,
    versions: {
      current: { releaseDate }
    },
    ossEnabled,
    licenses,
    policies: {
      restrictedLicense,
      restrictedByOrg,
      invalidVersion,
      versionRestriction
    }
  },
  className
}: LibraryDetailsProps) => {
  const { formatMessage } = useIntl()
  const { isStaticGrid } = useLibrariesPageContext()

  return (
    <TextBar className={className}>
      <TextBarItem
        testId="library-details-release-date"
        label={formatMessage({ id: 'title_libraries_versionTypeReleased' })}
        value={
          !!releaseDate ? (
            <LibraryReleaseDate
              releaseDate={releaseDate}
              showParenthesis={false}
            />
          ) : null
        }
      />
      <TextBarItem
        testId="library-details-hash"
        label={formatMessage({ id: 'detail_identifier' })}
        value={hash}
      />
      {ossEnabled || isStaticGrid ? (
        <LibraryLicenses licenses={licenses}>
          {({ label, value }) => (
            <TextBarItem
              testId="library-details-licenses"
              label={label as string}
              value={value}
            />
          )}
        </LibraryLicenses>
      ) : undefined}
      {showRestrictionIndicator({
        restrictedLicense,
        restrictedByOrg,
        versionRestriction
      }) ? (
        <span
          data-testid="library-details-restrictions"
          className={css`
            display: flex;
            margin-bottom: 3px;
            align-items: center;
            max-width: 100%;
            overflow: hidden;
          `}
        >
          <RestrictionIcon
            disableLeftMargin
            policies={{
              restrictedByOrg,
              restrictedLicense,
              invalidVersion,
              versionRestriction
            }}
          />
          <Text
            className={css`
              margin-left: 4px;
            `}
            color="red70"
            font="metadata"
          >
            {formatMessage({ id: 'title_policyViolation' })}
          </Text>
        </span>
      ) : undefined}
    </TextBar>
  )
}
